import { BonePose, clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Boss clips. Every boss has idle, walk (loops), attack (the blow lands at ~0.15 s, when the simulation deals
 * it; the weight goes into the recovery), hit (they barely flinch), stun (loop), die (ends on the ground; the
 * view then sinks it), roar, and its own powers as one-shots. They are huge: slow, planted, every blow held a
 * beat on contact and settled with a spring, and the hanging bits (lantern, hair, shell slab, crystals) trail.
 *
 * Conventions as in human.ts: limbs hang along -Y, so a negative X swings an arm or leg forward; +X on a shin
 * bends the knee back, -X on a forearm bends the elbow forward; +X on a trunk leans it forward; +X on a jaw
 * opens it; the figure faces +Z and its `R` limbs are on +X.
 */

type Pose<N extends string> = (b: Bones<N>, t: number) => void;

/** A pose sheet that makes any bone it is asked for (for sampling a pose at another time). */
const scratch = <N extends string>(): Bones<N> => {
  const o: Record<string, BonePose> = {};
  return new Proxy(o, { get: (_, k: string) => (o[k] ??= new BonePose()) }) as Bones<N>;
};

/** A bone that trails its chain: `hang` cancels that much of the chain's tilt (1 hangs plumb), `k` swings it against the motion. */
interface Follow<N extends string> {
  bone: N;
  chain: readonly N[];
  lag: number;
  k: number;
  hang?: number;
}

/**
 * Secondary motion: samples the pose a beat (and two) earlier and swings each follower against how its chain
 * moved in between, overshooting once, so a lantern swings back as the staff jabs and a shell slab nods on
 * every footfall. Deterministic: it only re-reads the pose itself.
 */
const follow = <N extends string>(pose: Pose<N>, dur: number, loop: boolean, fs: readonly Follow<N>[]): Pose<N> => (b, t) => {
  pose(b, t);
  const wrap = (x: number): number => (loop ? ((x % dur) + dur) % dur : Math.max(0, x));
  const cache = new Map<number, Bones<N>>();
  const at = (x: number): Bones<N> => {
    const w = wrap(x);
    let s = cache.get(w);
    if (!s) {
      s = scratch<N>();
      pose(s, w);
      cache.set(w, s);
    }
    return s;
  };
  for (const f of fs) {
    const s1 = at(t - f.lag);
    const s2 = at(t - 2 * f.lag);
    const sum = (bb: Bones<N>, ax: 'rx' | 'ry' | 'rz'): number => f.chain.reduce((a, n) => a + bb[n][ax], 0);
    const bp = b[f.bone];
    for (const ax of ['rx', 'ry', 'rz'] as const) {
      const c0 = sum(b, ax), c1 = sum(s1, ax), c2 = sum(s2, ax);
      const hang = ax === 'ry' ? 0 : (f.hang ?? 0);
      bp[ax] += -hang * c0 - f.k * (c0 - c1) + 0.55 * f.k * (c1 - c2);
    }
  }
};

/** Held looks: a sine that lingers at its ends (a stare, then a slow turn). */
const held = (x: number, k = 0.45): number => {
  const s = Math.sin(x);
  return Math.sign(s) * Math.abs(s) ** k;
};

/** A deterministic shiver: two incommensurate-looking sines (at integer multiples of `f` for loops). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 1.7 * t + 1.3);

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** Adds `p` to every beat that leaves a bone out (so a timeline's beats only spell what changes from the stance). */
const over = <N extends string>(st: KeyPose<N>, beats: readonly (readonly [number, KeyPose<N>, Ease?])[]): Beat<N>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...st, ...p }, e] : [t, { ...st, ...p }]) as Beat<N>);

/* ====================================================================================================== */
/* ---------- La Bruja de Ceniza: hunched, hobbling on her lantern staff; she hexes and shrieks ---------- */

type WB = 'root' | 'robe' | 'chest' | 'head' | 'hair' | 'armL' | 'foreL' | 'staff' | 'lantern' | 'armR' | 'foreR';
type WP = KeyPose<WB>;

/** Hunched over the staff, head craned up to glare, the free claw raised. */
const W_ST: WP = {
  robe: [0.03, 0, 0],
  chest: [0.16, 0, 0],
  head: [-0.2, 0, 0],
  armL: [-0.06, 0, 0],
  staff: [0.05, 0, 0],
  armR: [-0.2, 0, 0.1],
  foreR: [-0.45, 0, 0],
};

/** The lantern hangs plumb off its hook and swings late; the hair trails the head. */
const W_FOLLOW: readonly Follow<WB>[] = [
  { bone: 'lantern', chain: ['root', 'robe', 'chest', 'armL', 'foreL', 'staff'], lag: 0.11, k: 1.3, hang: 1 },
  { bone: 'hair', chain: ['root', 'robe', 'chest', 'head'], lag: 0.09, k: 0.9, hang: 0.45 },
];

/**
 * The staff's X and Z channels are absolute: its tilt from straight up (+X tips the lantern forward, +Z out to
 * her left), whatever the arm does, so a staff raised overhead still points at the sky.
 */
const aimStaff = (pose: Pose<WB>): Pose<WB> => (b, t) => {
  pose(b, t);
  b.staff.rx -= b.root.rx + b.robe.rx + b.chest.rx + b.armL.rx + b.foreL.rx;
  b.staff.rz -= b.root.rz + b.robe.rz + b.chest.rz + b.armL.rz + b.foreL.rz;
};

const wclip = (name: string, dur: number, loop: boolean, pose: Pose<WB>, squash?: (t: number) => [number, number, number], label?: string): Clip<WB> =>
  clip<WB>(name, dur, loop, follow(aimStaff(pose), dur, loop, W_FOLLOW), squash, label);

const witchIdle = (): Clip<WB> =>
  wclip('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    apply(b, W_ST);
    const br = Math.sin(p * 2);
    // Ragged breaths, a slow sway over the staff, a glare that sweeps the field and lingers.
    b.robe.rot(0.012 * br, 0.04 * Math.sin(p), 0.022 * Math.sin(p));
    b.chest.rot(-0.035 * br, 0.05 * Math.sin(p + 0.7), 0.02 * Math.sin(p + 0.3));
    b.head.rot(0.03 * br + 0.04 * Math.sin(p * 3), 0.42 * held(p + 0.4), 0.09 * Math.sin(p + 1.1));
    b.armR.rot(0.05 * br, 0.08 * Math.sin(p + 2), 0.04 * Math.sin(p * 2));
    // The claw closes and opens, slowly, as if counting.
    b.foreR.rot(-0.16 * Math.max(0, Math.sin(p * 2 + 1)) ** 2);
    b.armL.rot(0.03 * Math.sin(p));
    b.staff.rot(-0.03 * Math.sin(p), 0, 0.02 * Math.sin(p));
    b.lantern.rot(0.04 * Math.sin(p * 2 + 0.5), 0, 0.07 * Math.sin(p + 1));
    b.root.pos(0, -0.008 * (1 + br), 0);
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.006 * br, 1 - 0.005 * br, 1 + 0.006 * br];
  });

/** A slow hobble: the robe rocks from side to side, the staff reaches out and is leaned on once a cycle. */
const witchWalk = (dur = 1.8): Clip<WB> =>
  wclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th);
    const c2 = Math.cos(2 * th);
    apply(b, W_ST, 0.9);
    b.robe.rot(0.05 + 0.03 * c2, 0.05 * sn, 0.075 * sn);
    b.root.pos(0, 0.022 * c2 - 0.014, 0);
    // The chest and head hold steady against the rocking.
    b.chest.rot(0.035 * Math.cos(2 * th - 0.6), -0.06 * sn, -0.055 * sn);
    b.head.rot(-0.03 * Math.cos(2 * th - 1.1), -0.03 * sn, -0.04 * sn);
    // The staff: swung forward, planted, leaned on as the body passes it.
    const reach = Math.sin(th + 0.6);
    b.armL.rot(-0.12 - 0.3 * reach, 0, -0.03 * sn);
    b.foreL.rot(-0.14 * Math.max(0, reach));
    b.staff.rot(0.26 * reach);
    b.armR.rot(0.18 * Math.sin(th - 0.4), 0, 0.04);
    b.foreR.rot(-0.12 * Math.max(0, -Math.sin(th - 0.8)));
    b.lantern.rot(0.06 * Math.sin(th - 1.4), 0, 0.06 * Math.sin(2 * th - 1));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.01 * k, 1 + 0.012 * k, 1 - 0.01 * k];
  });

/** Draws the lantern back and jabs it at her foe: the green fire spits on the jab (0.16 s), then she gathers again. */
const witchAttack = (): Clip<WB> =>
  wclip('attack', 0.9, false, (b, t) => {
    timeline(b, t, over(W_ST, [
      [0, {}],
      [0.08, { robe: [-0.04, 0, 0], chest: [-0.12, 0.28, 0], head: [-0.32, 0.12, 0], armL: [0.3, 0, -0.15], foreL: [-0.25, 0, 0], staff: [-0.35, 0, 0], armR: [-0.7, 0, 0.55], foreR: [-0.9, 0, 0] }, outQuad],
      [0.16, { robe: [0.1, 0, 0], chest: [0.48, -0.25, 0], head: [0.05, -0.1, 0], armL: [-1.25, 0, 0.1], foreL: [-0.35, 0, 0], staff: [0.95, 0, 0], armR: [0.35, 0, 0.3], foreR: [-0.15, 0, 0], root: [0, 0, 0, 0, -0.02, 0.08] }, inCubic],
      [0.36, { robe: [0.09, 0, 0], chest: [0.42, -0.22, 0], head: [0.0, -0.08, 0], armL: [-1.12, 0, 0.08], foreL: [-0.3, 0, 0], staff: [0.85, 0, 0], armR: [0.3, 0, 0.28], foreR: [-0.2, 0, 0], root: [0, 0, 0, 0, -0.018, 0.07] }, outBack],
      [0.9, {}, inOut],
    ]), { head: 0.03, foreL: 0.015, staff: 0.03, foreR: 0.035, armR: 0.02 });
  });

const witchHit = (): Clip<WB> =>
  wclip('hit', 0.35, false, (b, t) => {
    apply(b, W_ST);
    const k = settle(t, 2.6, 9) * span(t, 0, 0.03, outQuad);
    b.robe.rot(-0.04 * k);
    b.chest.rot(-0.15 * k, 0.08 * k);
    b.head.rot(-0.22 * k, -0.1 * k);
    b.armR.rot(0.25 * k, 0, 0.15 * k);
    b.root.pos(0, 0, -0.04 * k);
  });

/** Reeling: she sways round over the staff, head lolling, the claw hanging. */
const witchStun = (): Clip<WB> =>
  wclip('stun', 1.8, true, (b, t) => {
    const p = phase(t, 1.8) * TAU;
    apply(b, W_ST, 0.6);
    b.robe.rot(0.06 * Math.sin(p), 0, 0.08 * Math.cos(p));
    b.chest.rot(0.28 + 0.05 * Math.sin(p * 2), 0, -0.07 * Math.cos(p));
    b.head.rot(0.3 + 0.2 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.armR.rot(0.25 + 0.1 * Math.sin(p + 1.2), 0, 0.05);
    b.foreR.rot(0.15);
    b.armL.rot(-0.1 * Math.sin(p));
    b.staff.rot(0.06 * Math.sin(p), 0, -0.1 * Math.cos(p));
  });

/** Struck down: she rears back shrieking, doubles over clutching her chest, the rags give and she pitches onto her face; the staff falls aside. */
const witchDie = (): Clip<WB> =>
  wclip('die', 2.4, false, (b, t) => {
    timeline(b, t, [
      [0, W_ST],
      [0.2, { robe: [-0.12, 0, 0.04], chest: [-0.45, 0.15, 0], head: [-0.65, 0.2, 0.1], armL: [-0.45, 0, -0.5], foreL: [-0.3, 0, 0], staff: [-0.3, 0, 0.1], armR: [-0.9, 0, 1.0], foreR: [-0.5, 0, 0], root: [0, 0, 0, 0, 0.02, -0.06] }, outExpo],
      [0.75, { robe: [0.12, 0, -0.06], chest: [0.65, -0.1, 0], head: [0.45, 0.1, 0], armL: [-0.25, 0, 0.05], foreL: [-0.1, 0, 0], staff: [0.05, 0, 0.05], armR: [-1.1, 0, -0.55], foreR: [-1.5, 0, 0], root: [0, 0, 0, 0, -0.04, 0] }, inOut],
      [1.2, { robe: [0.2, 0, 0.12, 0, 0, 0, 0.8], chest: [0.7, 0.2, 0.1], head: [0.6, 0.3, 0.15], armL: [-0.3, 0, 0.2], foreL: [-0.2, 0, 0], staff: [0.2, 0, 0.5], armR: [-0.9, 0, -0.4], foreR: [-1.2, 0, 0], root: [0, 0, 0, 0, -0.02, 0] }, inOut],
      [1.72, { robe: [1.42, 0, 0.1, 0, 0, 0, 0.74], chest: [0.3, 0.1, 0], head: [-0.25, 0.6, 0.2], armL: [-1.9, 0, -0.4], foreL: [0, 0, 0], staff: [0.3, 0, 1.5], armR: [-2.2, 0, 0.3], foreR: [-0.2, 0, 0], root: [0, 0, 0, 0, 0.1, 0] }, inQuad],
      [2.4, { robe: [1.38, 0, 0.1, 0, 0, 0, 0.74], chest: [0.32, 0.1, 0], head: [-0.2, 0.62, 0.22], armL: [-1.95, 0, -0.42], foreL: [0.05, 0, 0], staff: [0.3, 0, 1.55], armR: [-2.25, 0, 0.32], foreR: [-0.15, 0, 0], root: [0, 0, 0, 0, 0.1, 0] }, outBack],
    ], { head: 0.04, foreR: 0.03, armR: 0.02, staff: 0.06 });
  });

/** Gathers herself, then flings the staff high and shrieks, shaking. */
const witchRoar = (): Clip<WB> =>
  wclip('roar', 1.6, false, (b, t) => {
    timeline(b, t, over(W_ST, [
      [0, {}],
      [0.32, { robe: [0.08, 0, 0], chest: [0.48, 0, 0], head: [0.38, 0, 0], armL: [0.1, 0, 0], armR: [0.2, 0, 0.3], foreR: [-0.7, 0, 0], root: [0, 0, 0, 0, -0.04, 0] }, inOut],
      [0.56, { robe: [-0.1, 0, 0], chest: [-0.42, 0, 0], head: [-0.72, 0, 0], armL: [-2.3, 0, -0.3], foreL: [-0.3, 0, 0], staff: [-0.12, 0, 0], armR: [-1.6, 0, 1.1], foreR: [-0.3, 0, 0], root: [0, 0, 0, 0, 0.04, 0] }, outBack],
      [1.15, { robe: [-0.08, 0, 0], chest: [-0.38, 0, 0], head: [-0.66, 0, 0], armL: [-2.25, 0, -0.3], foreL: [-0.3, 0, 0], staff: [-0.1, 0, 0], armR: [-1.55, 0, 1.05], foreR: [-0.35, 0, 0], root: [0, 0, 0, 0, 0.035, 0] }],
      [1.6, {}, inOut],
    ]), { head: 0.03, foreR: 0.03, staff: 0.04 });
    const tr = envelope(t, 0.5, 0.6, 1.1, 1.3) * shiver(t, 9);
    b.chest.rot(0, 0.03 * tr, 0.025 * tr);
    b.head.rot(0.03 * tr, 0.05 * tr);
    b.foreR.rot(0.08 * tr);
  }, undefined, 'rugido');

/** The curse: she draws back, then thrusts the lantern at the tower with her claw splayed, straining, as the hex flies (0.2 s). */
const witchCurse = (): Clip<WB> =>
  wclip('curse', 1.15, false, (b, t) => {
    timeline(b, t, over(W_ST, [
      [0, {}],
      [0.1, { chest: [-0.1, 0.3, 0], head: [-0.3, 0.2, 0], armL: [0.2, 0, -0.2], staff: [-0.35, 0, 0], armR: [-0.4, 0, 0.6], foreR: [-1.0, 0, 0] }, outQuad],
      [0.2, { robe: [0.06, 0, 0], chest: [0.32, -0.2, 0], head: [0.02, -0.15, 0], armL: [-1.6, 0, 0.15], foreL: [-0.25, 0, 0], staff: [1.0, 0, 0], armR: [-1.35, 0, -0.15], foreR: [-0.2, 0, 0], root: [0, 0, 0, 0, 0, 0.05] }, inCubic],
      [0.68, { robe: [0.05, 0, 0], chest: [0.3, -0.18, 0], head: [0.0, -0.13, 0], armL: [-1.5, 0, 0.12], foreL: [-0.22, 0, 0], staff: [0.95, 0, 0], armR: [-1.28, 0, -0.12], foreR: [-0.25, 0, 0], root: [0, 0, 0, 0, 0, 0.045] }, outQuad],
      [1.15, {}, inOut],
    ]), { head: 0.025, staff: 0.03, foreR: 0.03 });
    const tr = envelope(t, 0.18, 0.24, 0.6, 0.72) * shiver(t, 11);
    b.armL.rot(0.025 * tr, 0, 0.02 * tr);
    b.armR.rot(0.03 * tr, 0.03 * tr);
    b.foreR.rot(0.06 * tr);
  }, undefined, 'maldición');

/** The summoning: the staff goes up in both hands and is driven into the ground (0.36 s), then she rises, arms spread, calling them up. */
const witchSummon = (): Clip<WB> =>
  wclip('summon', 1.8, false, (b, t) => {
    timeline(b, t, over(W_ST, [
      [0, {}],
      [0.26, { robe: [-0.06, 0, 0], chest: [-0.3, 0, 0], head: [-0.5, 0, 0], armL: [-2.4, 0, 0.15], foreL: [-0.25, 0, 0], staff: [0.0, 0, 0], armR: [-2.2, 0, -0.3], foreR: [-0.5, 0, 0], root: [0, 0, 0, 0, 0.05, 0] }, outQuad],
      [0.37, { robe: [0.12, 0, 0], chest: [0.62, 0, 0], head: [0.3, 0, 0], armL: [-0.75, 0, 0.1], foreL: [-0.35, 0, 0], staff: [0.1, 0, 0], armR: [-0.85, 0, -0.25], foreR: [-0.55, 0, 0], root: [0, 0, 0, 0, -0.06, 0] }, inCubic],
      [0.62, { robe: [0.1, 0, 0], chest: [0.56, 0, 0], head: [0.24, 0, 0], armL: [-0.72, 0, 0.1], foreL: [-0.32, 0, 0], staff: [0.08, 0, 0], armR: [-0.8, 0, -0.2], foreR: [-0.5, 0, 0], root: [0, 0, 0, 0, -0.05, 0] }, outBack],
      [1.0, { robe: [-0.05, 0, 0], chest: [-0.35, 0, 0], head: [-0.62, 0, 0], armL: [-0.12, 0, -0.1], foreL: [-0.1, 0, 0], staff: [0.05, 0, 0], armR: [-1.0, 0, 1.25], foreR: [-0.2, 0, 0], root: [0, 0, 0, 0, 0.02, 0] }, inOut],
      [1.35, { robe: [-0.04, 0, 0], chest: [-0.32, 0, 0], head: [-0.58, 0, 0], armL: [-0.1, 0, -0.1], foreL: [-0.1, 0, 0], staff: [0.05, 0, 0], armR: [-0.95, 0, 1.2], foreR: [-0.25, 0, 0], root: [0, 0, 0, 0, 0.02, 0] }],
      [1.8, {}, inOut],
    ]), { head: 0.03, foreR: 0.03, armR: 0.03 });
    const tr = envelope(t, 0.95, 1.05, 1.3, 1.45) * shiver(t, 10);
    b.armR.rot(0.03 * tr, 0, 0.03 * tr);
    b.head.rot(0.025 * tr);
  }, (t) => thud(t, [[0.37, 0.03]]), 'invocación');

export const witchClips = (): Clip[] => [witchIdle(), witchWalk(), witchAttack(), witchHit(), witchStun(), witchDie(), witchRoar(), witchCurse(), witchSummon()] as Clip[];

/* ====================================================================================================== */
/* ---------- El Caparazón Hueco: a hollow shell on six bone legs, slow, deliberate, insect-wrong ---------- */

const LEGS = [0, 1, 2, 3, 4, 5] as const;
type SB = 'root' | 'carapace' | `leg${(typeof LEGS)[number]}` | `knee${(typeof LEGS)[number]}`;
type SP = KeyPose<SB>;
/** Each leg's angle round the shell (0 → +X, π/2 → front). */
const LEG_A = LEGS.map((i) => (i / 6) * TAU + 0.3);
const legName = (i: number): SB => `leg${i}` as SB;
const kneeName = (i: number): SB => `knee${i}` as SB;
const front = (i: number): number => Math.sin(LEG_A[i] as number);
const right = (i: number): number => Math.cos(LEG_A[i] as number);

/**
 * Leg pose: `lift` raises the knee (and tucks the foot), `stride` swings the foot forward (+) or back (−),
 * `splay` drops the knee and pushes the foot out; `thigh` and `knee` add raw turns. Each leg's bones are turned
 * so local +Z runs out along it: −X lifts the thigh, +X on the knee tucks the foot in, −X reaches it out.
 */
const legCh = (i: number, lift: number, stride: number, splay = 0, thigh = 0, knee = 0): Record<string, readonly [number, number, number]> => ({
  [legName(i)]: [-0.38 * lift + splay + thigh, -0.24 * stride * right(i), 0],
  [kneeName(i)]: [0.32 * lift - 0.2 * stride * front(i) - 0.7 * splay + knee, 0, 0],
});

type LegArgs = [number, number, number?, number?, number?];

/** A key pose of all six legs from a function of the leg index, plus the carapace. */
const legsPose = (fn: (i: number) => LegArgs, carapace?: readonly number[]): SP => {
  const p: Record<string, readonly number[]> = {};
  for (const i of LEGS) Object.assign(p, legCh(i, ...fn(i)));
  if (carapace) p.carapace = carapace;
  return p as SP;
};

const addLeg = (b: Bones<SB>, i: number, lift: number, stride: number, splay = 0): void => {
  for (const [k, c] of Object.entries(legCh(i, lift, stride, splay))) b[k as SB].rot(c[0], c[1], c[2]);
};

/** Low on its legs, knees a little bowed out. */
const S_ST: SP = legsPose(() => [0, 0, 0.06], [0, 0, 0, 0, -0.02, 0]);

const sclip = (name: string, dur: number, loop: boolean, pose: Pose<SB>, squash?: (t: number) => [number, number, number], label?: string): Clip<SB> =>
  clip<SB>(name, dur, loop, pose, squash, label);

/** It breathes through the hollow; one leg after another lifts and resettles, a slow ripple, as the shell turns to look. */
const shellIdle = (): Clip<SB> =>
  sclip('idle', 4.2, true, (b, t) => {
    const ph = phase(t, 4.2);
    const p = ph * TAU;
    apply(b, S_ST);
    const br = Math.sin(p * 2);
    b.carapace.rot(0.02 * Math.sin(p * 2 + 1), 0.09 * held(p, 0.6), 0.015 * Math.sin(p)).pos(0, 0.014 * br, 0);
    for (const i of LEGS) {
      // Each leg's turn to shift: a lift and a small re-plant, in order round the shell.
      const d = (((ph - i / 6) % 1) + 1) % 1;
      const k = d < 0.14 ? Math.sin((d / 0.14) * Math.PI) : 0;
      addLeg(b, i, 0.5 * k, 0.25 * k * Math.sin(p + i), 0);
      b[legName(i)].rot(-0.02 * br);
    }
  }, (t) => {
    const br = Math.sin(phase(t, 4.2) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.01 * br, 1 + 0.008 * br];
  });

/** A ponderous tripod gait: three legs swing while three drag the shell forward; it rolls toward the planted side. */
const shellWalk = (dur = 2): Clip<SB> =>
  sclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, S_ST, 0.8);
    for (const i of LEGS) {
      const ph = th + (i % 2 ? Math.PI : 0) + 0.25 * (i % 3);
      const lift = Math.max(0, Math.cos(ph)) ** 1.5;
      addLeg(b, i, 0.85 * lift, Math.sin(ph), 0.05);
    }
    const c2 = Math.cos(2 * th);
    b.carapace.rot(0.025 * Math.cos(2 * th - 0.5), 0.035 * Math.sin(th), 0.035 * Math.sin(th)).pos(0, -0.012 - 0.016 * c2, 0);
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, dur) * TAU * 2)) ** 3;
    return [1 + 0.012 * k, 1 - 0.018 * k, 1 + 0.012 * k];
  });

/** Rears its front off the ground and crashes down on its foe (0.16 s), front legs stabbing; it rocks back and settles. */
const shellAttack = (): Clip<SB> =>
  sclip('attack', 1.0, false, (b, t) => {
    timeline(b, t, [
      [0, S_ST],
      [0.08, legsPose((i) => (front(i) > 0.2 ? [1.1, 0.6, 0] : [0, -0.3, 0.12]), [-0.3, 0, 0, 0, 0.08, -0.06]), outQuad],
      [0.16, legsPose((i) => (front(i) > 0.2 ? [-0.2, 0.7, 0.25] : [0.1, -0.2, 0]), [0.2, 0, 0, 0, -0.07, 0.13]), inCubic],
      [0.4, legsPose((i) => (front(i) > 0.2 ? [-0.15, 0.6, 0.2] : [0.05, -0.15, 0.02]), [0.14, 0, 0, 0, -0.05, 0.1]), outBack],
      [1.0, S_ST, inOut],
    ], { knee1: 0.02, knee0: 0.03, knee2: 0.03 });
  }, (t) => thud(t, [[0.16, 0.05]]));

const shellHit = (): Clip<SB> =>
  sclip('hit', 0.35, false, (b, t) => {
    apply(b, S_ST);
    const k = settle(t, 2.4, 9) * span(t, 0, 0.03, outQuad);
    b.carapace.rot(-0.06 * k, 0, 0.03 * k).pos(0, 0.02 * k, -0.03 * k);
    for (const i of LEGS) b[legName(i)].rot(-0.08 * k);
  });

/** Dazed: the shell wobbles round on legs that twitch out of time. */
const shellStun = (): Clip<SB> =>
  sclip('stun', 1.8, true, (b, t) => {
    const p = phase(t, 1.8) * TAU;
    apply(b, S_ST);
    b.carapace.rot(0.06 * Math.sin(p), 0.05 * Math.sin(p + 1), 0.07 * Math.cos(p)).pos(0, -0.04, 0);
    for (const i of LEGS) {
      b[legName(i)].rot(0.12 + 0.1 * Math.sin(p * 2 + i * 1.3));
      b[kneeName(i)].rot(-0.08 + 0.15 * Math.sin(p * 3 + i * 1.7));
    }
  });

/** It shudders, the legs on one side give, then the rest; the shell crashes onto its rim, a last leg twitches and curls. */
const shellDie = (): Clip<SB> =>
  sclip('die', 2.4, false, (b, t) => {
    timeline(b, t, [
      [0, S_ST],
      [0.16, legsPose(() => [0.35, 0, 0], [-0.12, 0, 0.05, 0, 0.06, 0]), outExpo],
      [0.72, legsPose((i) => (right(i) > 0.1 ? [0, 0, 0, -0.32, -0.2] : [0, 0, 0, -0.05, 0.02]), [0.04, 0, -0.17, 0, -0.22, 0]), inOut],
      [1.25, legsPose((i) => [0, 0, 0, -0.62, -0.28 + 0.03 * i], [0.07, 0, -0.06, 0, -0.52, 0]), inQuad],
      [1.5, legsPose((i) => [0, 0, 0, -0.58, -0.3 + 0.03 * i], [0.06, 0, -0.05, 0, -0.5, 0]), outBack],
      [2.4, legsPose((i) => [0, 0, 0, -0.56, -0.18 + 0.03 * i], [0.06, 0, -0.05, 0, -0.5, 0]), inOut],
    ], { leg2: 0.06, leg3: 0.09, leg4: 0.04, knee2: 0.08, knee3: 0.11 });
    // A last spasm: one leg jerks up and curls, then falls still.
    const tw = envelope(t, 1.72, 1.82, 1.95, 2.3, outQuad);
    addLeg(b, 2, 1.4 * tw, -0.3 * tw, 0);
    b.knee2.rot(0.6 * tw);
    const sh = envelope(t, 0.1, 0.2, 0.6, 0.75) * shiver(t, 8);
    b.carapace.rot(0.02 * sh, 0, 0.02 * sh);
  }, (t) => thud(t, [[1.25, 0.06]]));

/** It rears up on its hind legs, front legs clawing the air, holds, and crashes down. */
const shellRoar = (): Clip<SB> =>
  sclip('roar', 1.6, false, (b, t) => {
    timeline(b, t, [
      [0, S_ST],
      [0.32, legsPose(() => [0, 0, 0.2], [0.08, 0, 0, 0, -0.06, 0]), inOut],
      [0.62, legsPose((i) => (front(i) > 0.2 ? [1.6, 0.8, 0] : [0, -0.4, 0.15]), [-0.45, 0, 0, 0, 0.12, -0.06]), outBack],
      [1.1, legsPose((i) => (front(i) > 0.2 ? [1.5, 0.7, 0] : [0, -0.38, 0.14]), [-0.42, 0, 0, 0, 0.11, -0.05])],
      [1.28, legsPose((i) => (front(i) > 0.2 ? [-0.1, 0.5, 0.25] : [0, -0.2, 0.05]), [0.12, 0, 0, 0, -0.06, 0.06]), inCubic],
      [1.6, S_ST, inOut],
    ], { knee0: 0.03, knee1: 0.02, knee2: 0.04 });
    const tr = envelope(t, 0.6, 0.66, 1.05, 1.12);
    for (const i of LEGS) if (front(i) > 0.2) b[legName(i)].rot(0.22 * tr * Math.sin(TAU * 5 * t + i * 2), 0.12 * tr * Math.sin(TAU * 4 * t + i));
    b.carapace.rot(0.02 * tr * shiver(t, 9));
  }, (t) => thud(t, [[1.28, 0.05]]), 'rugido');

/** The spit: it squeezes down, then heaves its front up and retches the shades out of the hollow (0.22 s). */
const shellSpit = (): Clip<SB> =>
  sclip('spit', 1.2, false, (b, t) => {
    timeline(b, t, [
      [0, S_ST],
      [0.12, legsPose(() => [0, 0, 0.18], [0.1, 0, 0, 0, -0.06, 0, 0.96]), inOut],
      [0.22, legsPose((i) => (front(i) > 0.2 ? [0.35, 0.2, 0] : [0, -0.15, 0.1]), [-0.26, 0, 0, 0, 0.06, -0.04, 1.05]), outExpo],
      [0.5, legsPose((i) => (front(i) > 0.2 ? [0.2, 0.15, 0.05] : [0, -0.1, 0.08]), [-0.15, 0, 0, 0, 0.03, -0.02, 1.0]), outQuad],
      [1.2, S_ST, inOut],
    ]);
  }, undefined, 'escupe sombras');

/** The bone walls: it stamps one side, then the other, and the floor answers. */
const shellWalls = (): Clip<SB> =>
  sclip('walls', 1.6, false, (b, t) => {
    const L = (i: number): boolean => right(i) < -0.1;
    const R = (i: number): boolean => right(i) > 0.1;
    timeline(b, t, [
      [0, S_ST],
      [0.3, legsPose((i) => (L(i) ? [1.3, 0.3, 0] : [0, 0, 0.15]), [0, 0, 0.12, 0, 0.05, 0]), inOut],
      [0.4, legsPose((i) => (L(i) ? [-0.1, 0.2, 0.25] : [0, 0, 0.1]), [0.03, 0, -0.05, 0, -0.07, 0]), inCubic],
      [0.75, legsPose((i) => (R(i) ? [1.3, 0.3, 0] : [0, 0, 0.15]), [0, 0, -0.12, 0, 0.05, 0]), inOut],
      [0.85, legsPose((i) => (R(i) ? [-0.1, 0.2, 0.25] : [0, 0, 0.1]), [0.03, 0, 0.05, 0, -0.07, 0]), inCubic],
      [1.05, legsPose(() => [0, 0, 0.1], [0.02, 0, 0.02, 0, -0.05, 0]), outBack],
      [1.6, S_ST, inOut],
    ]);
  }, (t) => thud(t, [[0.4, 0.05], [0.85, 0.05]]), 'muros de hueso');

/** Enraged: it shudders end to end, legs thrashing, rises and slams itself down. */
const shellEnrage = (): Clip<SB> =>
  sclip('enrage', 1.4, false, (b, t) => {
    timeline(b, t, [
      [0, S_ST],
      [0.25, legsPose(() => [0, 0, 0.2], [0.05, 0, 0, 0, -0.06, 0]), inOut],
      [0.8, legsPose(() => [0.3, 0, -0.1], [-0.12, 0, 0, 0, 0.1, 0]), outQuad],
      [0.95, legsPose(() => [-0.1, 0, 0.25], [0.08, 0, 0, 0, -0.08, 0]), inCubic],
      [1.4, S_ST, inOut],
    ]);
    const tr = envelope(t, 0.15, 0.3, 0.8, 0.95);
    b.carapace.rot(0.04 * tr * shiver(t, 9), 0.05 * tr * shiver(t + 0.3, 7), 0.05 * tr * shiver(t + 0.1, 10));
    for (const i of LEGS) b[legName(i)].rot(-0.3 * tr * Math.max(0, Math.sin(TAU * 6 * t + i * 1.9)), 0.15 * tr * Math.sin(TAU * 5 * t + i));
  }, (t) => thud(t, [[0.95, 0.06]]), 'furia');

export const shellClips = (): Clip[] => [shellIdle(), shellWalk(), shellAttack(), shellHit(), shellStun(), shellDie(), shellRoar(), shellSpit(), shellWalls(), shellEnrage()] as Clip[];

/* ====================================================================================================== */
/* ---------- El Custodio de la Médula: a hunched brute of bone, knuckles low, the shell slab riding its back ---------- */

type KB = 'root' | 'hips' | 'chest' | 'shell' | 'head' | 'jaw' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type KP = KeyPose<KB>;

/** Crouched, hunched, fists hanging by the knees, the skull lifted to look ahead. */
const K_ST: KP = {
  root: [0, 0, 0, 0, -0.02, 0],
  hips: [0.05, 0, 0],
  chest: [0.12, 0, 0],
  head: [-0.16, 0, 0],
  armL: [-0.08, 0, -0.06],
  armR: [-0.08, 0, 0.06],
  foreL: [-0.18, 0, 0],
  foreR: [-0.18, 0, 0],
  legL: [-0.12, 0, -0.03],
  legR: [-0.12, 0, 0.03],
  shinL: [0.22, 0, 0],
  shinR: [0.22, 0, 0],
};

/** The slab nods late on every move of the back. */
const K_FOLLOW: readonly Follow<KB>[] = [{ bone: 'shell', chain: ['root', 'hips', 'chest'], lag: 0.08, k: 0.8 }];

const kclip = (name: string, dur: number, loop: boolean, pose: Pose<KB>, squash?: (t: number) => [number, number, number], label?: string): Clip<KB> =>
  clip<KB>(name, dur, loop, follow(pose, dur, loop, K_FOLLOW), squash, label);

const wardenIdle = (): Clip<KB> =>
  kclip('idle', 4.4, true, (b, t) => {
    const p = phase(t, 4.4) * TAU;
    apply(b, K_ST);
    const br = Math.sin(p * 2);
    // Deep breaths that lift the shoulders; the weight rolls from leg to leg; the skull sweeps the hall and stares.
    b.chest.rot(-0.04 * br, 0.04 * Math.sin(p + 0.5), 0.02 * Math.sin(p));
    b.hips.rot(0, 0.04 * Math.sin(p), 0.03 * Math.sin(p));
    b.root.pos(0.012 * Math.sin(p), -0.008 * (1 + br), 0);
    b.legL.rot(0, 0, -0.025 * Math.sin(p));
    b.legR.rot(0, 0, -0.025 * Math.sin(p));
    b.head.rot(0.04 * br + 0.04 * Math.sin(p * 3), 0.45 * held(p + 0.3), 0.06 * Math.sin(p + 1));
    b.jaw.rot(0.1 * Math.max(0, Math.sin(p * 3)) ** 3);
    b.armL.rot(0.03 * br, 0, -0.03 * br);
    b.armR.rot(0.03 * br, 0, 0.03 * br);
    b.foreL.rot(-0.07 * Math.sin(p * 2 + 1));
    b.foreR.rot(-0.07 * Math.sin(p * 2 + 2));
    b.shell.rot(0.02 * Math.sin(p * 2 - 0.8));
  }, (t) => {
    const br = Math.sin(phase(t, 4.4) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.006 * br, 1 + 0.008 * br];
  });

/** A heavy knuckle-dragging stride: it drops onto each foot, the shoulders roll against the hips, the slab bounces. */
const wardenWalk = (dur = 1.6): Clip<KB> =>
  kclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const c2 = Math.cos(2 * th);
    const land = Math.max(0, -c2) ** 4;
    apply(b, K_ST, 0.9);
    const A = 0.38;
    b.legL.rot(-A * sn - 0.05);
    b.legR.rot(A * sn - 0.05);
    b.shinL.rot(0.9 * Math.max(0, cs) ** 1.5 + 0.12 + 0.12 * land);
    b.shinR.rot(0.9 * Math.max(0, -cs) ** 1.5 + 0.12 + 0.12 * land);
    b.root.pos(0, 0.03 * c2 - 0.03 - 0.025 * land, 0);
    b.hips.rot(0.02 * land, 0.07 * sn, 0.05 * sn);
    b.chest.rot(0.06 + 0.03 * Math.cos(2 * th - 0.5), -0.13 * sn, -0.04 * sn);
    b.head.rot(-0.03 * Math.cos(2 * th - 0.9), 0.06 * sn, 0.04 * sn);
    b.armL.rot(0.36 * Math.sin(th - 0.5), 0, -0.08);
    b.armR.rot(-0.36 * Math.sin(th - 0.5), 0, 0.08);
    b.foreL.rot(-0.25 * Math.max(0, -Math.sin(th - 0.8)));
    b.foreR.rot(-0.25 * Math.max(0, Math.sin(th - 0.8)));
    b.jaw.rot(0.07 * land);
    b.shell.rot(-0.05 * Math.max(0, -Math.cos(2 * th - 0.6)) ** 3);
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, dur) * TAU * 2)) ** 4;
    return [1 + 0.02 * k, 1 - 0.025 * k, 1 + 0.02 * k];
  });

/** The cleave: a short coil and a backhanded sweep of the right fist across its front (0.16 s); it drags through and recovers. */
const wardenAttack = (): Clip<KB> =>
  kclip('attack', 1.0, false, (b, t) => {
    timeline(b, t, over(K_ST, [
      [0, {}],
      [0.08, { hips: [0.02, 0.15, 0], chest: [0.0, 0.45, 0.1], head: [-0.22, -0.2, 0], armR: [-0.6, 0, 1.1], foreR: [-0.6, 0, 0], armL: [0.2, 0, -0.3], foreL: [-0.3, 0, 0], legR: [0.02, 0, 0.03], shinR: [0.3, 0, 0], root: [0, 0, 0, 0, -0.02, -0.02] }, outQuad],
      [0.16, { hips: [0.1, -0.25, 0], chest: [0.3, -0.5, -0.1], head: [-0.05, 0.25, 0], jaw: [0.35, 0, 0], armR: [-1.3, 0, -0.5], foreR: [-0.15, 0, 0], armL: [0.25, 0, -0.2], foreL: [-0.2, 0, 0], legL: [-0.35, 0, -0.03], shinL: [0.4, 0, 0], legR: [0.1, 0, 0.03], shinR: [0.25, 0, 0], root: [0, 0, 0, 0, -0.06, 0.08] }, inCubic],
      [0.42, { hips: [0.1, -0.3, 0], chest: [0.34, -0.6, -0.1], head: [-0.02, 0.2, 0], jaw: [0.2, 0, 0], armR: [-1.1, 0, -0.75], foreR: [-0.2, 0, 0], armL: [0.22, 0, -0.2], foreL: [-0.2, 0, 0], legL: [-0.33, 0, -0.03], shinL: [0.38, 0, 0], legR: [0.08, 0, 0.03], shinR: [0.24, 0, 0], root: [0, 0, 0, 0, -0.055, 0.075] }, outCubic],
      [1.0, {}, inOut],
    ]), { foreR: 0.02, head: 0.035, jaw: 0.03, armL: 0.02 });
  }, (t) => thud(t, [[0.16, 0.03]]));

const wardenHit = (): Clip<KB> =>
  kclip('hit', 0.35, false, (b, t) => {
    apply(b, K_ST);
    const k = settle(t, 2.4, 9) * span(t, 0, 0.03, outQuad);
    b.chest.rot(-0.08 * k, 0.05 * k);
    b.head.rot(-0.18 * k, -0.08 * k);
    b.jaw.rot(0.2 * Math.abs(k));
    b.root.pos(0, 0, -0.03 * k);
  });

/** Reeling: the skull lolls, the arms swing loose, the knees sag and it sways round its feet. */
const wardenStun = (): Clip<KB> =>
  kclip('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, K_ST, 0.7);
    b.root.rot(0.04 * Math.sin(p), 0, 0.06 * Math.cos(p));
    b.chest.rot(0.25 + 0.06 * Math.sin(p * 2), 0.1 * Math.sin(p), -0.08 * Math.cos(p));
    b.head.rot(0.3 + 0.15 * Math.sin(p + 0.8), 0.35 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.jaw.rot(0.3);
    b.armL.rot(0.12 * Math.sin(p + 1.4), 0, -0.06 * Math.cos(p + 1));
    b.armR.rot(-0.12 * Math.sin(p + 1.4), 0, 0.06 * Math.cos(p + 1));
    b.shinL.rot(0.15);
    b.shinR.rot(0.15 + 0.1 * Math.sin(p));
    b.legR.rot(-0.05 * Math.sin(p));
  });

/** It rears back roaring, staggers a step, drops to its knees on its fists, sags, and pitches onto its face, the slab slumping askew on its back. */
const wardenDie = (): Clip<KB> =>
  kclip('die', 2.6, false, (b, t) => {
    const kneel: KP = { legL: [0.05, 0, -0.06], shinL: [1.55, 0, 0], legR: [0.0, 0, 0.06], shinR: [1.55, 0, 0] };
    timeline(b, t, [
      [0, K_ST],
      [0.3, { hips: [-0.1, 0, 0], chest: [-0.42, 0.1, 0], head: [-0.62, 0.2, 0], jaw: [0.8, 0, 0], armL: [-0.9, 0, -0.9], foreL: [-0.4, 0, 0], armR: [-0.7, 0, 1.0], foreR: [-0.5, 0, 0], legL: [-0.1, 0, -0.03], legR: [0.1, 0, 0.03], shinL: [0.2, 0, 0], shinR: [0.3, 0, 0], root: [0, 0, 0, 0, 0, -0.06] }, outExpo],
      [0.75, { legR: [0.35, 0, 0.03], shinR: [0.35, 0, 0], legL: [-0.25, 0, -0.03], shinL: [0.3, 0, 0], hips: [0.1, -0.1, 0.05], chest: [0.3, -0.1, 0.1], head: [0.2, 0.3, 0.1], jaw: [0.4, 0, 0], armL: [0.1, 0, -0.3], foreL: [-0.2, 0, 0], armR: [0.2, 0, 0.3], foreR: [-0.2, 0, 0], root: [0, 0, 0.04, 0, -0.06, -0.15] }, inOut],
      [1.25, { ...kneel, root: [0, 0, 0, 0, -0.28, -0.12], hips: [0.2, 0, 0], chest: [0.35, 0, 0.05], head: [0.45, 0.1, 0], jaw: [0.3, 0, 0], armL: [-0.45, 0, -0.2], foreL: [-0.1, 0, 0], armR: [-0.4, 0, 0.2], foreR: [-0.1, 0, 0] }, inQuad],
      [1.45, { ...kneel, root: [0, 0, 0, 0, -0.27, -0.12], hips: [0.22, 0, 0], chest: [0.4, 0, 0.05], head: [0.5, 0.1, 0], jaw: [0.35, 0, 0], armL: [-0.42, 0, -0.2], foreL: [-0.12, 0, 0], armR: [-0.38, 0, 0.2], foreR: [-0.12, 0, 0] }, outBack],
      [1.95, { ...kneel, root: [0, 0, 0, 0, -0.28, -0.12], hips: [0.3, 0, 0.04], chest: [0.55, 0, 0.08], head: [0.7, 0.2, 0.1], jaw: [0.45, 0, 0], armL: [-0.25, 0, -0.25], foreL: [-0.1, 0, 0], armR: [-0.2, 0, 0.25], foreR: [-0.1, 0, 0] }, inOut],
      [2.35, { ...kneel, root: [0, 0, 0, 0, -0.32, 0], hips: [1.2, 0, 0.1], chest: [0.45, 0, 0.1], head: [0.3, 0.4, 0.2], jaw: [0.4, 0, 0], armL: [-2.4, 0, -0.5], foreL: [0, 0, 0], armR: [-2.2, 0, 0.6], foreR: [0, 0, 0], shell: [0.15, 0, 0.42, 0.1, -0.04, 0.06] }, inQuad],
      [2.6, { ...kneel, root: [0, 0, 0, 0, -0.32, 0], hips: [1.16, 0, 0.1], chest: [0.43, 0, 0.1], head: [0.28, 0.42, 0.22], jaw: [0.42, 0, 0], armL: [-2.42, 0, -0.52], foreL: [0.02, 0, 0], armR: [-2.22, 0, 0.62], foreR: [0.02, 0, 0], shell: [0.16, 0, 0.46, 0.11, -0.04, 0.07] }, outBack],
    ], { head: 0.04, jaw: 0.03, foreL: 0.03, foreR: 0.03, shell: 0.06 });
  }, (t) => thud(t, [[1.25, 0.04], [2.35, 0.04]]));

/** Coils low, then bursts upright, arms flung wide, jaw gaping, and shakes with the roar (the sim's two seconds of roaring). */
const wardenRoar = (): Clip<KB> =>
  kclip('roar', 1.8, false, (b, t) => {
    timeline(b, t, over(K_ST, [
      [0, {}],
      [0.4, { hips: [0.15, 0, 0], chest: [0.42, 0, 0], head: [0.4, 0, 0], armL: [0.15, 0, 0.2], foreL: [-0.9, 0, 0], armR: [0.15, 0, -0.2], foreR: [-0.9, 0, 0], legL: [-0.22, 0, -0.03], legR: [-0.22, 0, 0.03], shinL: [0.42, 0, 0], shinR: [0.42, 0, 0], root: [0, 0, 0, 0, -0.07, 0] }, inOut],
      [0.6, { hips: [-0.08, 0, 0], chest: [-0.45, 0, 0], head: [-0.65, 0, 0], jaw: [0.9, 0, 0], armL: [-1.2, 0, -1.1], foreL: [-0.5, 0, 0], armR: [-1.2, 0, 1.1], foreR: [-0.5, 0, 0], legL: [-0.08, 0, -0.03], legR: [-0.08, 0, 0.03], shinL: [0.12, 0, 0], shinR: [0.12, 0, 0], root: [0, 0, 0, 0, 0.02, 0] }, outBack],
      [1.35, { hips: [-0.07, 0, 0], chest: [-0.42, 0, 0], head: [-0.6, 0, 0], jaw: [0.85, 0, 0], armL: [-1.15, 0, -1.05], foreL: [-0.55, 0, 0], armR: [-1.15, 0, 1.05], foreR: [-0.55, 0, 0], legL: [-0.08, 0, -0.03], legR: [-0.08, 0, 0.03], shinL: [0.13, 0, 0], shinR: [0.13, 0, 0], root: [0, 0, 0, 0, 0.018, 0] }],
      [1.8, {}, inOut],
    ]), { head: 0.03, jaw: 0.02, foreL: 0.03, foreR: 0.03 });
    const tr = envelope(t, 0.55, 0.65, 1.3, 1.45) * shiver(t, 11);
    b.chest.rot(0, 0.025 * tr, 0.02 * tr);
    b.head.rot(0.03 * tr, 0.04 * tr);
    b.jaw.rot(0.06 * tr);
  }, (t) => thud(t, [[0.6, -0.03]]), 'rugido');

/** Reading the floor: both fists up, a double drumbeat on the stone (0.36 s, 0.68 s), and the marks bloom where it listens. */
const wardenCast = (): Clip<KB> =>
  kclip('cast', 1.3, false, (b, t) => {
    const slam: KP = { hips: [0.25, 0, 0], chest: [0.55, 0, 0], head: [0.2, 0, 0], jaw: [0.5, 0, 0], armL: [-0.75, 0, -0.1], foreL: [-0.1, 0, 0], armR: [-0.75, 0, 0.1], foreR: [-0.1, 0, 0], legL: [-0.3, 0, -0.03], legR: [-0.3, 0, 0.03], shinL: [0.55, 0, 0], shinR: [0.55, 0, 0], root: [0, 0, 0, 0, -0.12, 0.06] };
    timeline(b, t, over(K_ST, [
      [0, {}],
      [0.26, { chest: [-0.15, 0, 0], head: [-0.32, 0, 0], armL: [-2.3, 0, -0.25], foreL: [-0.5, 0, 0], armR: [-2.3, 0, 0.25], foreR: [-0.5, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0, 0] }, outQuad],
      [0.36, slam, inCubic],
      [0.48, { ...slam, chest: [0.5, 0, 0], root: [0, 0, 0, 0, -0.11, 0.06] }, outBack],
      [0.58, { ...slam, chest: [0.32, 0, 0], armL: [-1.45, 0, -0.15], armR: [-1.45, 0, 0.15], foreL: [-0.45, 0, 0], foreR: [-0.45, 0, 0], jaw: [0.2, 0, 0], root: [0, 0, 0, 0, -0.08, 0.05] }, inOut],
      [0.68, { ...slam, armL: [-0.8, 0, -0.1], armR: [-0.8, 0, 0.1] }, inCubic],
      [0.86, { ...slam, chest: [0.5, 0, 0], root: [0, 0, 0, 0, -0.11, 0.06] }, outBack],
      [1.3, {}, inOut],
    ]), { head: 0.03, jaw: 0.02, foreL: 0.02, foreR: 0.03 });
  }, (t) => thud(t, [[0.36, 0.05], [0.68, 0.035]]), 'lee el suelo');

/** The ember slam: both fists hoisted together overhead, a held beat, and the hammer-blow into the floor (0.68 s) that wakes the fire. */
const wardenSlam = (): Clip<KB> =>
  kclip('slam', 1.5, false, (b, t) => {
    const hit: KP = { hips: [0.3, 0, 0], chest: [0.6, 0, 0], head: [0.25, 0, 0], jaw: [0.6, 0, 0], armL: [-0.9, 0, 0.1], foreL: [-0.1, 0, 0], armR: [-0.9, 0, -0.1], foreR: [-0.1, 0, 0], legL: [-0.35, 0, -0.03], legR: [-0.35, 0, 0.03], shinL: [0.6, 0, 0], shinR: [0.6, 0, 0], root: [0, 0, 0, 0, -0.14, 0.1] };
    timeline(b, t, over(K_ST, [
      [0, {}],
      [0.45, { hips: [-0.08, 0, 0], chest: [-0.35, 0, 0], head: [-0.38, 0, 0], jaw: [0.2, 0, 0], armL: [-2.9, 0, 0.35], foreL: [-0.6, 0, 0], armR: [-2.9, 0, -0.35], foreR: [-0.6, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.02, -0.04] }, inOut],
      [0.56, { hips: [-0.1, 0, 0], chest: [-0.42, 0, 0], head: [-0.42, 0, 0], jaw: [0.3, 0, 0], armL: [-3.0, 0, 0.35], foreL: [-0.65, 0, 0], armR: [-3.0, 0, -0.35], foreR: [-0.65, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.025, -0.05] }, outQuad],
      [0.68, hit, inCubic],
      [0.95, { ...hit, chest: [0.56, 0, 0], root: [0, 0, 0, 0, -0.13, 0.1] }, outBack],
      [1.5, {}, inOut],
    ]), { head: 0.03, jaw: 0.02, foreL: 0.02, foreR: 0.02 });
    const sh = kick(t, 0.68, 6, 8);
    b.root.pos(0.01 * sh, 0, 0);
  }, (t) => thud(t, [[0.68, 0.06]]), 'golpe de magma');

export const wardenClips = (): Clip[] => [wardenIdle(), wardenWalk(), wardenAttack(), wardenHit(), wardenStun(), wardenDie(), wardenRoar(), wardenCast(), wardenSlam()] as Clip[];

/* ====================================================================================================== */
/* ---------- El Señor del Magma: a lord of basalt that leans on, drags and brings down a molten maul ---------- */

type MB = 'root' | 'body' | 'head' | 'jaw' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'maul' | 'legL' | 'shinL' | 'legR' | 'shinR';
type MP = KeyPose<MB>;
/** The maul's rest angle in the hand (the builder's 1.2 rad, the haft tipped forward). */
const MAUL_REST = 1.2;

/** Hunched over the maul, its head resting on the ground before him; the free fist hangs. */
const M_ST: MP = {
  root: [0, 0, 0, 0, -0.02, 0],
  body: [0.12, 0, 0],
  head: [-0.1, 0, 0],
  armL: [-0.1, 0, -0.08],
  foreL: [-0.25, 0, 0],
  armR: [-0.25, 0, 0.06],
  foreR: [-0.2, 0, 0],
  maul: [2.0, 0, 0],
  legL: [-0.1, 0, -0.03],
  shinL: [0.18, 0, 0],
  legR: [-0.06, 0, 0.03],
  shinR: [0.14, 0, 0],
};

/**
 * The maul's X channel is absolute: the haft's angle from straight up toward the front (π/2 points it ahead,
 * about 2 lays its head on the ground in front), whatever the arm and body do.
 */
const mclip = (name: string, dur: number, loop: boolean, pose: Pose<MB>, squash?: (t: number) => [number, number, number], label?: string): Clip<MB> =>
  clip<MB>(name, dur, loop, (b, t) => {
    pose(b, t);
    b.maul.rx -= MAUL_REST + b.armR.rx + b.foreR.rx + b.body.rx + b.root.rx;
  }, squash, label);

const magmaIdle = (): Clip<MB> =>
  mclip('idle', 4.4, true, (b, t) => {
    const p = phase(t, 4.4) * TAU;
    apply(b, M_ST);
    const br = Math.sin(p * 2);
    // Heaving breaths over the maul; the head lifts to glare and sinks again; the free fist clenches.
    b.body.rot(-0.035 * br, 0.04 * Math.sin(p), 0.02 * Math.sin(p + 0.4));
    b.root.pos(0.01 * Math.sin(p), -0.01 * (1 + br), 0);
    b.head.rot(-0.08 * Math.max(0, Math.sin(p + 0.6)) + 0.03 * br, 0.32 * held(p * 1 + 2), 0.05 * Math.sin(p + 1));
    b.jaw.rot(0.08 * Math.max(0, -br) ** 2);
    b.armL.rot(0.04 * br, 0, -0.03 * br);
    b.foreL.rot(-0.12 * Math.max(0, Math.sin(p * 2 + 1)) ** 2);
    b.armR.rot(0.02 * br);
    b.maul.rot(0.04 * Math.sin(p), 0, 0.04 * Math.sin(p + 0.5));
    b.legL.rot(0, 0, -0.02 * Math.sin(p));
    b.legR.rot(0, 0, -0.02 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 4.4) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.007 * br, 1 + 0.008 * br];
  });

/** Lumbering forward, dropping onto each foot, dragging the maul's head along the ground beside him. */
const magmaWalk = (dur = 1.9): Clip<MB> =>
  mclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const c2 = Math.cos(2 * th);
    const land = Math.max(0, -c2) ** 4;
    apply(b, M_ST, 0.9);
    const A = 0.34;
    b.legL.rot(-A * sn - 0.04);
    b.legR.rot(A * sn - 0.04);
    b.shinL.rot(0.85 * Math.max(0, cs) ** 1.5 + 0.1 + 0.1 * land);
    b.shinR.rot(0.85 * Math.max(0, -cs) ** 1.5 + 0.1 + 0.1 * land);
    b.root.pos(0, 0.032 * c2 - 0.035 - 0.03 * land, 0);
    b.body.rot(0.05 + 0.03 * Math.cos(2 * th - 0.5) + 0.02 * land, -0.1 * sn, -0.045 * sn);
    b.head.rot(-0.035 * Math.cos(2 * th - 0.9), 0.06 * sn, 0.04 * sn);
    b.armL.rot(0.32 * Math.sin(th - 0.5), 0, -0.06);
    b.foreL.rot(-0.2 * Math.max(0, -Math.sin(th - 0.8)));
    b.armR.rot(-0.08 * Math.sin(th - 0.5), 0, 0.03);
    b.maul.rot(0.08 + 0.05 * Math.cos(2 * th - 0.4), 0, 0.05 * Math.sin(th - 0.6));
    b.jaw.rot(0.05 * land);
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, dur) * TAU * 2)) ** 4;
    return [1 + 0.022 * k, 1 - 0.028 * k, 1 + 0.022 * k];
  });

/** A low sweep of the maul from his right across his front (0.16 s); he carries through and heaves it back to rest. */
const magmaAttack = (): Clip<MB> =>
  mclip('attack', 1.1, false, (b, t) => {
    timeline(b, t, over(M_ST, [
      [0, {}],
      [0.08, { body: [0.05, 0.45, 0.05], head: [-0.15, -0.25, 0], armR: [-0.3, 0, 0.7], foreR: [-0.3, 0, 0], maul: [1.55, 0, 0], armL: [-0.3, 0, -0.3], foreL: [-0.4, 0, 0], root: [0, 0, 0, 0, -0.02, -0.03] }, outQuad],
      [0.16, { body: [0.25, -0.55, -0.08], head: [0, 0.2, 0], jaw: [0.35, 0, 0], armR: [-1.2, 0, -0.55], foreR: [-0.2, 0, 0], maul: [1.7, 0, 0], armL: [0.2, 0, -0.25], legL: [-0.35, 0, -0.03], shinL: [0.4, 0, 0], legR: [0.1, 0, 0.03], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.06, 0.08] }, inCubic],
      [0.4, { body: [0.3, -0.7, -0.1], head: [0.02, 0.25, 0], jaw: [0.2, 0, 0], armR: [-1.0, 0, -0.8], foreR: [-0.2, 0, 0], maul: [1.95, 0, 0], armL: [0.2, 0, -0.25], legL: [-0.33, 0, -0.03], shinL: [0.38, 0, 0], legR: [0.08, 0, 0.03], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.06, 0.08] }, outCubic],
      [0.75, { body: [0.22, -0.2, 0], armR: [-0.5, 0, -0.1], maul: [2.1, 0, 0], root: [0, 0, 0, 0, -0.04, 0.04] }, inOut],
      [1.1, {}, inOut],
    ]), { maul: 0.04, head: 0.035, jaw: 0.03, armL: 0.02 });
  }, (t) => thud(t, [[0.16, 0.025]]));

const magmaHit = (): Clip<MB> =>
  mclip('hit', 0.35, false, (b, t) => {
    apply(b, M_ST);
    const k = settle(t, 2.4, 9) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.07 * k, 0.05 * k);
    b.head.rot(-0.16 * k, -0.08 * k);
    b.jaw.rot(0.15 * Math.abs(k));
    b.maul.rot(-0.04 * k);
    b.root.pos(0, 0, -0.03 * k);
  });

/** Reeling, sagging on the maul, the head rolling. */
const magmaStun = (): Clip<MB> =>
  mclip('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, M_ST, 0.8);
    b.maul.rot(0.45 + 0.05 * Math.sin(p));
    b.root.rot(0.03 * Math.sin(p), 0, 0.05 * Math.cos(p));
    b.body.rot(0.28 + 0.05 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.06 * Math.cos(p));
    b.head.rot(0.3 + 0.15 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.jaw.rot(0.25);
    b.armL.rot(0.12 * Math.sin(p + 1.4), 0, -0.05);
    b.shinL.rot(0.12);
    b.shinR.rot(0.12 + 0.08 * Math.sin(p));
  });

/** Rocked back, the maul slips in his grip, he drops to one knee leaning on it, sags, and topples over. */
const magmaDie = (): Clip<MB> =>
  mclip('die', 2.6, false, (b, t) => {
    const knee: MP = { legR: [0.25, 0, 0.03], shinR: [1.6, 0, 0], legL: [-0.9, 0, -0.05], shinL: [1.1, 0, 0] };
    timeline(b, t, [
      [0, M_ST],
      [0.3, { body: [-0.35, 0.15, 0.1], head: [-0.5, 0.2, 0], jaw: [0.7, 0, 0], armL: [-0.6, 0, -0.8], foreL: [-0.5, 0, 0], armR: [-0.5, 0, 0.5], foreR: [-0.3, 0, 0], maul: [1.4, 0, 0], legL: [0.1, 0, -0.03], shinL: [0.2, 0, 0], legR: [-0.15, 0, 0.03], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, 0, -0.08] }, outExpo],
      [0.7, { body: [0.25, 0, 0.05], head: [0.3, 0, 0], jaw: [0.3, 0, 0], armL: [-0.3, 0, -0.3], foreL: [-0.2, 0, 0], armR: [0.1, 0, 0.15], foreR: [0, 0, 0], maul: [2.3, 0, 0], legL: [0, 0, -0.03], shinL: [0.2, 0, 0], legR: [0, 0, 0.03], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.04, -0.06] }, inOut],
      [1.2, { ...knee, root: [0, 0, 0, 0, -0.24, -0.05], body: [0.4, 0.1, 0], head: [0.4, 0.2, 0.1], jaw: [0.25, 0, 0], armL: [-0.6, 0, -0.1], foreL: [-0.6, 0, 0], armR: [0.1, 0, 0.3], foreR: [0, 0, 0], maul: [1.9, 0, 0.1] }, inQuad],
      [1.4, { ...knee, root: [0, 0, 0, 0, -0.23, -0.05], body: [0.44, 0.1, 0], head: [0.45, 0.2, 0.1], jaw: [0.3, 0, 0], armL: [-0.58, 0, -0.1], foreL: [-0.62, 0, 0], armR: [0.1, 0, 0.3], foreR: [0, 0, 0], maul: [1.92, 0, 0.1] }, outBack],
      [1.9, { ...knee, root: [0, 0, 0, 0, -0.24, -0.05], body: [0.6, 0.15, 0.1], head: [0.6, 0.3, 0.15], jaw: [0.4, 0, 0], armL: [-0.4, 0, -0.1], foreL: [-0.5, 0, 0], armR: [0.15, 0, 0.35], foreR: [0, 0, 0], maul: [1.85, 0, 0.15] }, inOut],
      [2.35, { ...knee, root: [0.25, 0, 0.5, 0.1, -0.3, 0], body: [1.0, 0.2, 0.3], head: [0.3, 0.5, 0.3], jaw: [0.45, 0, 0], armL: [-1.6, 0, -0.3], foreL: [0, 0, 0], armR: [-1.4, 0, 0.6], foreR: [0, 0, 0], maul: [1.7, 0, 0.5] }, inQuad],
      [2.6, { ...knee, root: [0.24, 0, 0.48, 0.1, -0.3, 0], body: [0.97, 0.2, 0.3], head: [0.28, 0.52, 0.32], jaw: [0.45, 0, 0], armL: [-1.62, 0, -0.32], foreL: [0.02, 0, 0], armR: [-1.42, 0, 0.62], foreR: [0.02, 0, 0], maul: [1.7, 0, 0.5] }, outBack],
    ], { head: 0.04, jaw: 0.03, maul: 0.05, foreL: 0.03 });
  }, (t) => thud(t, [[1.2, 0.04], [2.35, 0.04]]));

/** Crouches, then rears up and lifts the maul high in one hand, the free fist clenched out to the side, roaring. */
const magmaRoar = (): Clip<MB> =>
  mclip('roar', 1.7, false, (b, t) => {
    timeline(b, t, over(M_ST, [
      [0, {}],
      [0.35, { body: [0.4, 0, 0], head: [0.35, 0, 0], armL: [0.1, 0, 0.2], foreL: [-1.0, 0, 0], armR: [-0.1, 0, 0.1], foreR: [-0.4, 0, 0], maul: [2.2, 0, 0], legL: [-0.2, 0, -0.03], legR: [-0.2, 0, 0.03], shinL: [0.35, 0, 0], shinR: [0.35, 0, 0], root: [0, 0, 0, 0, -0.07, 0] }, inOut],
      [0.62, { body: [-0.4, 0, 0], head: [-0.6, 0, 0], jaw: [0.85, 0, 0], armR: [-2.8, 0, 0.25], foreR: [-0.2, 0, 0], maul: [-0.1, 0, 0], armL: [-0.5, 0, -0.9], foreL: [-0.9, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.02, 0] }, outBack],
      [1.25, { body: [-0.37, 0, 0], head: [-0.55, 0, 0], jaw: [0.8, 0, 0], armR: [-2.75, 0, 0.25], foreR: [-0.22, 0, 0], maul: [-0.12, 0, 0], armL: [-0.48, 0, -0.85], foreL: [-0.95, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.018, 0] }],
      [1.7, {}, inOut],
    ]), { head: 0.03, jaw: 0.02, maul: 0.05, foreL: 0.03 });
    const tr = envelope(t, 0.58, 0.68, 1.2, 1.35) * shiver(t, 10);
    b.body.rot(0, 0.02 * tr, 0.02 * tr);
    b.head.rot(0.03 * tr, 0.04 * tr);
    b.foreL.rot(0.05 * tr);
  }, undefined, 'rugido');

/** The eruption: he hoists the maul overhead in both hands, hangs a beat, and brings it down into the rock (0.73 s); the ground answers. */
const magmaErupt = (): Clip<MB> =>
  mclip('erupt', 1.6, false, (b, t) => {
    const slam: MP = { body: [0.55, 0, 0], head: [0.25, 0, 0], jaw: [0.6, 0, 0], armR: [-1.0, 0, -0.15], foreR: [-0.1, 0, 0], maul: [1.95, 0, 0], armL: [-1.0, 0, 0.35], foreL: [-0.15, 0, 0], legL: [-0.45, 0, -0.03], shinL: [0.6, 0, 0], legR: [0.1, 0, 0.03], shinR: [0.4, 0, 0], root: [0, 0, 0, 0, -0.13, 0.1] };
    timeline(b, t, over(M_ST, [
      [0, {}],
      [0.5, { body: [-0.3, -0.1, 0], head: [-0.35, 0, 0], jaw: [0.2, 0, 0], armR: [-2.7, 0, -0.2], foreR: [-0.5, 0, 0], maul: [-0.5, 0, 0], armL: [-2.5, 0, 0.3], foreL: [-0.6, 0, 0], legL: [-0.2, 0, -0.03], shinL: [0.2, 0, 0], legR: [0.05, 0, 0.03], shinR: [0.15, 0, 0], root: [0, 0, 0, 0, 0.03, -0.03] }, inOut],
      [0.6, { body: [-0.36, -0.1, 0], head: [-0.4, 0, 0], jaw: [0.3, 0, 0], armR: [-2.8, 0, -0.2], foreR: [-0.55, 0, 0], maul: [-0.68, 0, 0], armL: [-2.6, 0, 0.3], foreL: [-0.65, 0, 0], legL: [-0.2, 0, -0.03], shinL: [0.2, 0, 0], legR: [0.05, 0, 0.03], shinR: [0.15, 0, 0], root: [0, 0, 0, 0, 0.035, -0.035] }, outQuad],
      [0.73, slam, inCubic],
      [1.0, { ...slam, body: [0.5, 0, 0], maul: [2.0, 0, 0], root: [0, 0, 0, 0, -0.12, 0.1] }, outBack],
      [1.6, {}, inOut],
    ]), { maul: 0.035, head: 0.03, jaw: 0.02, foreL: 0.02 });
    const sh = kick(t, 0.73, 7, 8);
    b.root.pos(0.012 * sh, 0, 0);
    b.head.rot(0.06 * sh);
  }, (t) => thud(t, [[0.73, 0.06]]), 'erupción');

export const magmaClips = (): Clip[] => [magmaIdle(), magmaWalk(), magmaAttack(), magmaHit(), magmaStun(), magmaDie(), magmaRoar(), magmaErupt()] as Clip[];

/* ====================================================================================================== */
/* ---------- El Coloso de Obsidiana: a giant of black glass; slow as a glacier, crystals ringing on every step ---------- */

type CB = 'root' | 'body' | 'head' | 'crystL' | 'crystR' | 'spines' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type CP = KeyPose<CB>;

const C_ST: CP = {
  root: [0, 0, 0, 0, -0.02, 0],
  body: [0.1, 0, 0],
  head: [-0.08, 0, 0],
  armL: [-0.08, 0, -0.1],
  foreL: [-0.2, 0, 0],
  armR: [-0.08, 0, 0.1],
  foreR: [-0.2, 0, 0],
  legL: [-0.08, 0, -0.04],
  shinL: [0.15, 0, 0],
  legR: [-0.08, 0, 0.04],
  shinR: [0.15, 0, 0],
};

/** Both fists high overhead: where the shine leaves it and the slam starts. */
const C_UP: CP = {
  ...C_ST,
  body: [-0.3, 0, 0],
  head: [-0.25, 0, 0],
  armL: [-2.85, 0, 0.3],
  foreL: [-0.6, 0, 0],
  armR: [-2.85, 0, -0.3],
  foreR: [-0.6, 0, 0],
  legL: [-0.05, 0, -0.04],
  legR: [-0.05, 0, 0.04],
  shinL: [0.08, 0, 0],
  shinR: [0.08, 0, 0],
  root: [0, 0, 0, 0, 0.02, -0.03],
};

/** The crystal clusters and the back spines are stiff but lag and ring. */
const C_FOLLOW: readonly Follow<CB>[] = [
  { bone: 'crystL', chain: ['root', 'body'], lag: 0.07, k: 0.9 },
  { bone: 'crystR', chain: ['root', 'body'], lag: 0.08, k: 0.9 },
  { bone: 'spines', chain: ['root', 'body'], lag: 0.09, k: 0.7 },
];

const cclip = (name: string, dur: number, loop: boolean, pose: Pose<CB>, squash?: (t: number) => [number, number, number], label?: string): Clip<CB> =>
  clip<CB>(name, dur, loop, follow(pose, dur, loop, C_FOLLOW), squash, label);

/** Rings the crystals after an impulse. */
const ring = (b: Bones<CB>, k: number): void => {
  b.crystL.rot(0.05 * k, 0, -0.06 * k);
  b.crystR.rot(0.05 * k, 0, 0.06 * k);
  b.spines.rot(-0.06 * k);
};

const colossusIdle = (): Clip<CB> =>
  cclip('idle', 4.8, true, (b, t) => {
    const p = phase(t, 4.8) * TAU;
    apply(b, C_ST);
    const br = Math.sin(p * 2);
    // Glacial breaths, a grinding twist of the trunk, the shard of a head turning to stare.
    b.body.rot(-0.025 * br, 0.05 * Math.sin(p), 0.015 * Math.sin(p + 0.5));
    b.root.pos(0, -0.008 * (1 + br), 0);
    b.head.rot(0.03 * br, 0.4 * held(p + 1.2, 0.35), 0.06 * Math.sin(p + 2));
    b.armL.rot(0.03 * br, 0, -0.02 * br);
    b.armR.rot(0.03 * br, 0, 0.02 * br);
    b.foreL.rot(-0.08 * Math.max(0, Math.sin(p * 2 + 1)) ** 2);
    b.foreR.rot(-0.08 * Math.max(0, Math.sin(p * 2 + 2.5)) ** 2);
    b.legL.rot(0, 0, -0.015 * Math.sin(p));
    b.legR.rot(0, 0, -0.015 * Math.sin(p));
    ring(b, 0.25 * Math.sin(p * 6) * Math.max(0, Math.sin(p * 2 + 0.4)));
  }, (t) => {
    const br = Math.sin(phase(t, 4.8) * TAU * 2);
    return [1 + 0.006 * br, 1 - 0.005 * br, 1 + 0.006 * br];
  });

/** An inexorable stride: each foot comes down like a dropped stone, the body jolts and the crystals ring. */
const colossusWalk = (dur = 2): Clip<CB> =>
  cclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const c2 = Math.cos(2 * th);
    // Footfalls at a quarter and three quarters of the cycle, each a jolt that rings out before the next.
    const half = dur / 2;
    const jolt = settle((((t - dur / 4) % half) + half) % half, 2.6, 6);
    apply(b, C_ST, 0.9);
    const A = 0.32;
    b.legL.rot(-A * sn - 0.04);
    b.legR.rot(A * sn - 0.04);
    b.shinL.rot(0.8 * Math.max(0, cs) ** 1.5 + 0.1);
    b.shinR.rot(0.8 * Math.max(0, -cs) ** 1.5 + 0.1);
    b.root.pos(0, 0.028 * c2 - 0.03 - 0.025 * Math.max(0, jolt), 0);
    b.body.rot(0.04 + 0.025 * Math.cos(2 * th - 0.5) + 0.03 * jolt, -0.1 * sn, -0.04 * sn);
    b.head.rot(-0.03 * Math.cos(2 * th - 0.9) - 0.04 * jolt, 0.05 * sn, 0.035 * sn);
    b.armL.rot(0.24 * Math.sin(th - 0.5), 0, -0.06);
    b.armR.rot(-0.24 * Math.sin(th - 0.5), 0, 0.06);
    b.foreL.rot(-0.18 * Math.max(0, -Math.sin(th - 0.8)) + 0.04 * jolt);
    b.foreR.rot(-0.18 * Math.max(0, Math.sin(th - 0.8)) + 0.04 * jolt);
    ring(b, 0.8 * jolt);
  }, (t) => {
    const half = dur / 2;
    const k = Math.max(0, settle((((t - dur / 4) % half) + half) % half, 2.6, 9));
    return [1 + 0.02 * k, 1 - 0.028 * k, 1 + 0.02 * k];
  });

/** A clubbing hook of the right fist (0.16 s): short coil, the glass club swung across, carried through, then settled back. */
const colossusAttack = (): Clip<CB> =>
  cclip('attack', 1.1, false, (b, t) => {
    timeline(b, t, over(C_ST, [
      [0, {}],
      [0.08, { body: [0.05, 0.4, 0.05], head: [-0.1, -0.2, 0], armR: [0.2, 0, 0.7], foreR: [-0.5, 0, 0], armL: [-0.3, 0, -0.2], foreL: [-0.4, 0, 0], root: [0, 0, 0, 0, -0.02, -0.03] }, outQuad],
      [0.16, { body: [0.3, -0.45, -0.08], head: [0.05, 0.2, 0], armR: [-1.35, 0, -0.45], foreR: [-0.4, 0, 0], armL: [0.2, 0, -0.2], foreL: [-0.3, 0, 0], legL: [-0.3, 0, -0.04], shinL: [0.35, 0, 0], legR: [0.1, 0, 0.04], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.05, 0.08] }, inCubic],
      [0.42, { body: [0.32, -0.55, -0.1], head: [0.06, 0.22, 0], armR: [-1.2, 0, -0.7], foreR: [-0.3, 0, 0], armL: [0.18, 0, -0.2], foreL: [-0.3, 0, 0], legL: [-0.28, 0, -0.04], shinL: [0.33, 0, 0], legR: [0.08, 0, 0.04], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.05, 0.075] }, outCubic],
      [1.1, {}, inOut],
    ]), { foreR: 0.025, head: 0.035, armL: 0.02 });
    ring(b, kick(t, 0.16, 3, 6));
  }, (t) => thud(t, [[0.16, 0.025]]));

const colossusHit = (): Clip<CB> =>
  cclip('hit', 0.35, false, (b, t) => {
    apply(b, C_ST);
    const k = settle(t, 2.4, 9) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.06 * k, 0.04 * k);
    b.head.rot(-0.14 * k, -0.06 * k);
    b.root.pos(0, 0, -0.025 * k);
    ring(b, 0.8 * k);
  });

const colossusStun = (): Clip<CB> =>
  cclip('stun', 2.2, true, (b, t) => {
    const p = phase(t, 2.2) * TAU;
    apply(b, C_ST, 0.7);
    b.root.rot(0.03 * Math.sin(p), 0, 0.05 * Math.cos(p));
    b.body.rot(0.24 + 0.05 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.06 * Math.cos(p));
    b.head.rot(0.28 + 0.14 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.18 * Math.sin(p + 0.8));
    b.armL.rot(0.1 * Math.sin(p + 1.4), 0, -0.05 * Math.cos(p + 1));
    b.armR.rot(-0.1 * Math.sin(p + 1.4), 0, 0.05 * Math.cos(p + 1));
    b.shinL.rot(0.12);
    b.shinR.rot(0.12 + 0.08 * Math.sin(p));
  });

/** Jolted back, it staggers, falls to its knees on its fists, cracks, and topples onto its face; the crystals snap and the head breaks loose. */
const colossusDie = (): Clip<CB> =>
  cclip('die', 2.6, false, (b, t) => {
    const kneel: CP = { legL: [0, 0, -0.06], shinL: [1.55, 0, 0], legR: [0, 0, 0.06], shinR: [1.55, 0, 0] };
    timeline(b, t, [
      [0, C_ST],
      [0.3, { body: [-0.3, 0.2, 0.1], head: [-0.5, 0.3, 0], armL: [-0.7, 0, -0.7], foreL: [-0.4, 0, 0], armR: [-0.5, 0, 0.8], foreR: [-0.3, 0, 0], legL: [-0.05, 0, -0.04], legR: [0.05, 0, 0.04], shinL: [0.15, 0, 0], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, 0, -0.06] }, outExpo],
      [0.8, { legL: [0.3, 0, -0.04], shinL: [0.3, 0, 0], legR: [-0.25, 0, 0.04], shinR: [0.3, 0, 0], body: [0.3, -0.1, 0.1], head: [0.3, 0.2, 0], armL: [0.1, 0, -0.2], foreL: [-0.1, 0, 0], armR: [0.15, 0, 0.2], foreR: [-0.1, 0, 0], root: [0, 0, 0.05, 0, -0.05, -0.12] }, inOut],
      [1.3, { ...kneel, root: [0, 0, 0, 0, -0.3, -0.1], body: [0.35, 0, 0], head: [0.4, 0, 0], armL: [-0.4, 0, -0.15], foreL: [-0.1, 0, 0], armR: [-0.4, 0, 0.15], foreR: [-0.1, 0, 0] }, inQuad],
      [1.45, { ...kneel, root: [0, 0, 0, 0, -0.29, -0.1], body: [0.38, 0, 0], head: [0.44, 0, 0], armL: [-0.38, 0, -0.15], foreL: [-0.12, 0, 0], armR: [-0.38, 0, 0.15], foreR: [-0.12, 0, 0] }, outBack],
      [1.85, { ...kneel, root: [0, 0, 0, 0, -0.3, -0.1], body: [0.5, 0.1, 0.08], head: [0.7, 0.2, 0.2], armL: [-0.3, 0, -0.2], foreL: [-0.1, 0, 0], armR: [-0.25, 0, 0.2], foreR: [-0.1, 0, 0], crystL: [0.15, 0, -0.15], crystR: [0.15, 0, 0.15] }, inOut],
      [2.3, { ...kneel, root: [0, 0, 0, 0, -0.32, 0], body: [1.3, 0.1, 0.15], head: [0.4, 0.4, 0.3, 0.05, -0.1, 0.15], armL: [-2.5, 0, -0.4], foreL: [0, 0, 0], armR: [-2.5, 0, 0.4], foreR: [0, 0, 0], crystL: [0.3, 0, -0.5, 0, 0, 0, 0.85], crystR: [0.3, 0, 0.5, 0, 0, 0, 0.85], spines: [0, 0, 0, 0, 0, 0, 0.8] }, inQuad],
      [2.6, { ...kneel, root: [0, 0, 0, 0, -0.32, 0], body: [1.26, 0.1, 0.15], head: [0.6, 0.6, 0.5, 0.08, -0.12, 0.2], armL: [-2.52, 0, -0.42], foreL: [0.02, 0, 0], armR: [-2.52, 0, 0.42], foreR: [0.02, 0, 0], crystL: [0.32, 0, -0.52, 0, 0, 0, 0.85], crystR: [0.32, 0, 0.52, 0, 0, 0, 0.85], spines: [0, 0, 0, 0, 0, 0, 0.8] }, outBack],
    ], { head: 0.05, foreL: 0.03, foreR: 0.03 });
    ring(b, kick(t, 1.3, 3, 6) + kick(t, 2.3, 3, 6));
  }, (t) => thud(t, [[1.3, 0.04], [2.3, 0.05]]));

/** The guardian wakes: it sinks and gathers, arms crossed over the heart, then rises and spreads them, head thrown back. */
const colossusRoar = (): Clip<CB> =>
  cclip('roar', 1.7, false, (b, t) => {
    timeline(b, t, over(C_ST, [
      [0, {}],
      [0.4, { body: [0.38, 0, 0], head: [0.4, 0, 0], armL: [-1.0, 0, 0.5], foreL: [-1.4, 0, 0], armR: [-1.0, 0, -0.5], foreR: [-1.4, 0, 0], legL: [-0.2, 0, -0.04], legR: [-0.2, 0, 0.04], shinL: [0.35, 0, 0], shinR: [0.35, 0, 0], root: [0, 0, 0, 0, -0.07, 0] }, inOut],
      [0.68, { body: [-0.35, 0, 0], head: [-0.5, 0, 0], armL: [-0.7, 0, -1.25], foreL: [-0.35, 0, 0], armR: [-0.7, 0, 1.25], foreR: [-0.35, 0, 0], legL: [-0.04, 0, -0.04], legR: [-0.04, 0, 0.04], shinL: [0.08, 0, 0], shinR: [0.08, 0, 0], root: [0, 0, 0, 0, 0.025, 0] }, outBack],
      [1.25, { body: [-0.32, 0, 0], head: [-0.46, 0, 0], armL: [-0.66, 0, -1.2], foreL: [-0.38, 0, 0], armR: [-0.66, 0, 1.2], foreR: [-0.38, 0, 0], legL: [-0.04, 0, -0.04], legR: [-0.04, 0, 0.04], shinL: [0.08, 0, 0], shinR: [0.08, 0, 0], root: [0, 0, 0, 0, 0.022, 0] }],
      [1.7, {}, inOut],
    ]), { head: 0.04, foreL: 0.03, foreR: 0.03 });
    const tr = envelope(t, 0.62, 0.7, 1.2, 1.35) * shiver(t, 12);
    b.body.rot(0, 0.015 * tr, 0.015 * tr);
    ring(b, 0.6 * tr + kick(t, 0.68, 3, 6));
  }, undefined, 'despertar');

/** The glint (a second before the shine): it hunches over its heart, arms drawn in, and the glass begins to shiver. */
const colossusGlint = (): Clip<CB> =>
  cclip('glint', 1.0, false, (b, t) => {
    const hunch: CP = { body: [0.32, 0, 0], head: [0.42, 0, 0], armL: [-1.1, 0, 0.5], foreL: [-1.45, 0, 0], armR: [-1.1, 0, -0.5], foreR: [-1.45, 0, 0], legL: [-0.2, 0, -0.04], legR: [-0.2, 0, 0.04], shinL: [0.35, 0, 0], shinR: [0.35, 0, 0], root: [0, 0, 0, 0, -0.06, 0] };
    timeline(b, t, over(C_ST, [
      [0, {}],
      [0.45, hunch, inOut],
      [1.0, { ...hunch, body: [0.36, 0, 0], head: [0.46, 0, 0], armL: [-1.15, 0, 0.55], armR: [-1.15, 0, -0.55], root: [0, 0, 0, 0, -0.07, 0] }],
    ]), { head: 0.03, foreL: 0.03, foreR: 0.03 });
    const tr = span(t, 0.3, 1.0) * shiver(t, 13);
    b.body.rot(0.01 * tr, 0.012 * tr, 0.012 * tr);
    ring(b, 1.2 * tr);
  }, undefined, 'destello');

/**
 * The shine (three seconds, while blows bounce): it bursts open, arms flung wide, chest to the light, and holds
 * its mirror up to them; in the last moments it raises both fists high, where the slam takes over.
 */
const colossusShine = (): Clip<CB> =>
  cclip('shine', 3.0, false, (b, t) => {
    const open: CP = { body: [-0.25, 0, 0], head: [-0.45, 0, 0], armL: [-0.55, 0, -1.3], foreL: [-0.3, 0, 0], armR: [-0.55, 0, 1.3], foreR: [-0.3, 0, 0], legL: [-0.04, 0, -0.06], legR: [-0.04, 0, 0.06], shinL: [0.06, 0, 0], shinR: [0.06, 0, 0], root: [0, 0, 0, 0, 0.03, 0] };
    timeline(b, t, over(C_ST, [
      [0, {}],
      [0.25, open, outExpo],
      [2.15, { ...open, body: [-0.2, 0, 0], head: [-0.38, 0, 0], armL: [-0.6, 0, -1.22], armR: [-0.6, 0, 1.22] }, inOut],
      [2.8, C_UP, inOut],
      [3.0, { ...C_UP, armL: [-2.9, 0, 0.3], armR: [-2.9, 0, -0.3], body: [-0.33, 0, 0] }],
    ]), { head: 0.04, foreL: 0.04, foreR: 0.04 });
    // Holding the mirror: a slow turn to show it to all of them.
    const hold = envelope(t, 0.3, 0.6, 1.9, 2.2);
    b.body.rot(0, 0.12 * hold * Math.sin(TAU * (t - 0.3) / 1.9), 0);
    b.head.rot(0, -0.08 * hold * Math.sin(TAU * (t - 0.3) / 1.9), 0);
    ring(b, kick(t, 0.25, 3, 5));
  }, undefined, 'brillo');

/** The slam (when the shine ends): from fists overhead, both clubs come down into the glass (0.18 s) and throw the ring of shards. */
const colossusSlam = (): Clip<CB> =>
  cclip('slam', 1.4, false, (b, t) => {
    const hit: CP = { body: [0.62, 0, 0], head: [0.3, 0, 0], armL: [-0.95, 0, 0.12], foreL: [-0.1, 0, 0], armR: [-0.95, 0, -0.12], foreR: [-0.1, 0, 0], legL: [-0.35, 0, -0.04], legR: [-0.35, 0, 0.04], shinL: [0.6, 0, 0], shinR: [0.6, 0, 0], root: [0, 0, 0, 0, -0.15, 0.1] };
    timeline(b, t, over(C_ST, [
      [0, C_UP],
      [0.07, { ...C_UP, armL: [-2.95, 0, 0.3], armR: [-2.95, 0, -0.3], body: [-0.35, 0, 0] }, outQuad],
      [0.18, hit, inCubic],
      [0.5, { ...hit, body: [0.58, 0, 0], root: [0, 0, 0, 0, -0.14, 0.1] }, outBack],
      [1.4, {}, inOut],
    ]), { head: 0.03, foreL: 0.02, foreR: 0.02 });
    const sh = kick(t, 0.18, 7, 8);
    b.root.pos(0.012 * sh, 0, 0);
    ring(b, 1.4 * kick(t, 0.18, 3, 5));
  }, (t) => thud(t, [[0.18, 0.06]]), 'golpe de cristal');

export const colossusClips = (): Clip[] =>
  [colossusIdle(), colossusWalk(), colossusAttack(), colossusHit(), colossusStun(), colossusDie(), colossusRoar(), colossusGlint(), colossusShine(), colossusSlam()] as Clip[];

/** Clips by library kind (see ../rigs.ts). The variants (molten Warden, shining colossus) share their rig and clips. */
export const CLIPS: Record<string, () => Clip[]> = {
  witch: witchClips,
  shell: shellClips,
  warden: wardenClips,
  wardenEmber: wardenClips,
  magmaLord: magmaClips,
  colossus: colossusClips,
  colossusShine: colossusClips,
};
